import { ChevronDown, Copy, Printer } from 'lucide-react';
import { type KeyboardEvent, type MouseEvent, type ReactNode, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

/** Chip kecil di sebelah kode (status order, status produk, dst). */
export interface CardRowBadge {
  label: string;
  /** Warna background, boleh hex atau nilai dari NEWCOLOR. */
  bg?: string;
  color?: string;
  className?: string;
}

/** Satu kolom di panel info abu-abu (PEMBELI, KATEGORI, TOTAL KLIK LINK, dst). */
export interface CardRowField {
  key: string;
  label: string;
  value?: ReactNode;
  /** Jika true dan value berupa string, tampilkan tombol salin di sebelah value. */
  copyable?: boolean;
  /** Jika true, tampilkan tombol cetak di sebelah value (misal cetak resi). */
  isPrint?: boolean;
  /** Dipanggil saat tombol cetak diklik; tanpa ini tombol cetak nonaktif. */
  onPrint?: () => void;
  className?: string;
}

/** Satu blok ringkasan di kanan panel info (TOTAL BAYAR, SALDO SAAT INI, JUMLAH STOK, dst). */
export interface CardRowSummary {
  key: string;
  label: string;
  value?: ReactNode;
  className?: string;
  /** Override style nilai, misal 'text-black' untuk angka stok. Default biru. */
  valueClassName?: string;
}

export type CardRowActionVariant = 'outline' | 'danger' | 'primary' | 'success' | 'secondary';

/** Satu tombol aksi di sisi kanan kartu. */
export interface CardRowAction {
  key: string;
  label: string;
  variant?: CardRowActionVariant;
  disabled?: boolean;
  hidden?: boolean;
  className?: string;
  onClick?: () => void;
}

/** Override class per bagian kartu, untuk halaman yang butuh style berbeda. */
export interface CardRowClassNames {
  root?: string;
  header?: string;
  code?: string;
  badge?: string;
  subtitle?: string;
  info?: string;
  field?: string;
  fieldLabel?: string;
  fieldValue?: string;
  summaries?: string;
  summary?: string;
  summaryLabel?: string;
  summaryValue?: string;
  actions?: string;
  action?: string;
}

export interface CardRowProps {
  /** Kode utama, contoh: PUR-001, AF-001, ID-001 */
  code: ReactNode;
  badge?: CardRowBadge;
  /** Baris kedua di bawah kode: invoice, nama affiliate, judul buku, dst. */
  subtitle?: ReactNode;
  fields?: CardRowField[];
  summaries?: CardRowSummary[];
  actions?: CardRowAction[];
  onCopy?: (value: string) => void;
  className?: string;
  classNames?: CardRowClassNames;
  /** Konten tambahan di bawah baris utama. */
  children?: ReactNode;
  /** Toggle accordion saat kartu diklik; klik pada tombol, link, atau konten children diabaikan. */
  onAcction?: () => void;
  /** Class tambahan untuk ikon panah, misal 'rotate-180' saat terbuka. */
  styleArrow?: string;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const ACTION_VARIANT: Record<CardRowActionVariant, string> = {
  outline: 'border border-blue-cs-200 bg-white text-black hover:bg-[#F3F8FF]',
  danger: 'bg-text-danger-500 text-white hover:bg-text-danger-500/90',
  primary: 'bg-text-blue-200 text-white hover:bg-text-blue-200/90',
  secondary: 'bg-text-blue-100 text-white hover:bg-text-blue-100/90',
  success: 'bg-bg-success text-white hover:bg-bg-success/90',
};

const ACTION_BASE =
  'h-9 rounded-[8px] px-4 body1 disabled:opacity-100 disabled:bg-[#D9D9D9] disabled:text-white disabled:border-0';

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard?.writeText(value);
  } catch {
    // clipboard tidak tersedia, abaikan
  }
};

const CardRow = ({
  code,
  badge,
  subtitle,
  fields = [],
  summaries = [],
  actions = [],
  onCopy,
  className,
  classNames = {},
  children,
  styleArrow,
  onAcction,
}: CardRowProps) => {
  const [copied, setCopied] = useState<string | null>(null);
  const visibleActions = actions.filter((action) => !action.hidden);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Seluruh kartu men-toggle accordion, kecuali klik yang berasal dari elemen
  // interaktif (tombol salin, Detail, panah) atau dari konten children (tabel)
  const handleRootClick = (event: MouseEvent<HTMLElement>) => {
    if (!onAcction) return;
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, select, textarea')) return;
    if (bodyRef.current?.contains(target)) return;
    onAcction();
  };

  const handleRootKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!onAcction || event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onAcction();
    }
  };

  const handleCopy = (value: string) => {
    copyToClipboard(value);
    onCopy?.(value);
    setCopied(value);
    window.setTimeout(() => setCopied((prev) => (prev === value ? null : prev)), 1500);
  };

  return (
    <article
      role={onAcction ? 'button' : undefined}
      tabIndex={onAcction ? 0 : undefined}
      onClick={handleRootClick}
      onKeyDown={handleRootKeyDown}
      className={cn(
        'shadowCard w-full flex flex-col gap-3 p-3 bg-white',
        onAcction && 'cursor-pointer',
        className,
        classNames.root
      )}
    >
      <div className="flex flex-col gap-6 xl:flex-row xl:items-center">
        {/* Kode + badge + subtitle */}
        <div className={cn('flex flex-col gap-1 xl:w-[250px] shrink-0 min-w-0', classNames.header)}>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <span className={cn('title2bold text-black', classNames.code)}>{code}</span>
            {badge && (
              <span
                className={cn(
                  'rounded-[6px] px-2 py-1 body2medium whitespace-nowrap',
                  classNames.badge,
                  badge.className
                )}
                style={{ backgroundColor: badge.bg, color: badge.color }}
              >
                {badge.label}
              </span>
            )}
          </div>
          {subtitle && (
            <span className={cn('body1 text-text-grey truncate', classNames.subtitle)}>
              {subtitle}
            </span>
          )}
        </div>

        {/* Panel info */}
        {fields.length > 0 && (
          <div
            className={cn(
              'flex-1 min-w-0 flex justify-between  flex-wrap gap-x-6 gap-y-3 rounded-[8px] bg-[#F6F6F6] p-3',
              classNames.info
            )}
          >
            {fields.map((field) => {
              const isCopyable =
                field.copyable && typeof field.value === 'string' && field.value.length > 0;
              return (
                <div
                  key={field.key}
                  className={cn(
                    'flex flex-col  flex-none xl:flex-1 gap-1 min-w-0',
                    classNames.field,
                    field.className
                  )}
                >
                  <span
                    className={cn(
                      'body2 uppercase text-text-grey truncate ',
                      classNames.fieldLabel
                    )}
                  >
                    {field.label}
                  </span>
                  <span
                    className={cn(
                      'body1medium text-black inline-flex items-center gap-1 min-w-0',
                      classNames.fieldValue
                    )}
                  >
                    <span className="truncate title2">{field.value ?? '-'}</span>
                    {isCopyable && (
                      <button
                        type="button"
                        onClick={() => handleCopy(field.value as string)}
                        aria-label={`Salin ${field.label} ${field.value as string}`}
                        className="shrink-0 inline-flex items-center gap-1 text-text-blue-100 transition hover:opacity-80"
                      >
                        <Copy size={14} strokeWidth={1.8} />
                        {copied === field.value && <span className="caption">Tersalin</span>}
                      </button>
                    )}
                    {field.isPrint && (
                      <button
                        type="button"
                        onClick={field.onPrint}
                        // disabled={!field.onPrint}
                        aria-label={`Cetak ${field.label}`}
                        className="shrink-0 inline-flex items-center text-text-blue-100 transition hover:opacity-80 disabled:text-text-grey disabled:opacity-60"
                      >
                        <Printer size={14} strokeWidth={1.8} />
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Ringkasan (total bayar, saldo, stok, harga, dst) */}
        {summaries.length > 0 && (
          <div className={cn('flex flex-wrap gap-6 shrink-0', classNames.summaries)}>
            {summaries.map((summary) => (
              <div
                key={summary.key}
                className={cn(
                  'flex flex-col gap-1 xl:items-end',
                  classNames.summary,
                  summary.className
                )}
              >
                <span className={cn('body2 uppercase text-text-grey', classNames.summaryLabel)}>
                  {summary.label}
                </span>
                <span
                  className={cn(
                    'title2bold text-text-blue-100 whitespace-nowrap',
                    classNames.summaryValue,
                    summary.valueClassName
                  )}
                >
                  {summary.value ?? '-'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Aksi */}
        {visibleActions.length > 0 && (
          <div
            className={cn('inline-flex items-center gap-2 flex-wrap shrink-0', classNames.actions)}
          >
            {visibleActions.map((action) => (
              <Button
                key={action.key}
                type="button"
                disabled={action.disabled}
                onClick={action.onClick}
                className={cn(
                  'flex-1',
                  ACTION_BASE,
                  ACTION_VARIANT[action.variant ?? 'outline'],
                  classNames.action,
                  action.className
                )}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {onAcction && (
          <button
            type="button"
            onClick={onAcction}
            aria-label="Buka detail"
            className="shrink-0 cursor-pointer text-black transition hover:opacity-80"
          >
            <ChevronDown className={cn('w-fit h-fit transition-transform', styleArrow)} />
          </button>
        )}
      </div>

      {/* Klik di dalam area ini tidak men-toggle accordion */}
      {children && <div ref={bodyRef}>{children}</div>}
    </article>
  );
};

export default CardRow;
